import React, { useMemo, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Event } from '@mui/icons-material';
import { OuterLinkIcon } from '@uplift/icons';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { UserStatus } from '@uplift/types';
import { AsyncButton, ClickToCopy } from '@uplift/ui-v2';

import { DeactivateDialog } from './DeactivateDialog';
import { MoreOptions, MoreOptionsProps } from './MoreOptions';
import { ProfileTabProps, ProfileTabs } from './ProfileTabs';

type ProfileHeaderProps = {
  className?: string;
  id: string;
  profileLabel?: string;
  userRole?: string;
  name: string;
  avatarUrl?: string;
  pronouns?: string;
  preferredName?: string;
  createdAt?: string;
  lastLogin?: string;
  options?: MoreOptionsProps['options'];
  chips?: React.ReactNode | React.ReactNode[];
  active?: boolean;
  tabs: ProfileTabProps[];
  tab: string;
  onTabChange: (tab: string) => void;
  onImpersonate?: () => void;
  onActiveStatusChange?: (active: boolean) => void;
  meta?: Record<string, string>;
  status?: UserStatus;
  providerNPI?: string;
  lastReferralDate?: string;
  upcomingAppointmentDate?: string;
};

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  className,
  id,
  profileLabel,
  userRole,
  name,
  preferredName,
  pronouns,
  avatarUrl,
  lastLogin,
  chips,
  options = {},
  active,
  tabs,
  tab,
  onTabChange,
  onImpersonate,
  onActiveStatusChange,
  meta,
  providerNPI,
  createdAt,
  lastReferralDate,
  upcomingAppointmentDate,
}) => {
  const { classes, cx } = useProfileHeaderStyles();
  const [ deactivateModalOpen, setDeactivateModalOpen ] = useState<boolean>(false);

  const handleActivate = async () => {
    if (active) {
      setDeactivateModalOpen(true);
    } else {
      if (onActiveStatusChange) {
        onActiveStatusChange(true);
      }
    }
  };

  if (onActiveStatusChange) {
    options = {
      [active ? 'Deactivate account' : 'Reactivate account']: handleActivate,
      ...(options || {}),
    };
  }

  const lastLoginValue = useMemo(() => {
    if (typeof lastLogin === 'string') {
      return CalendarDateService.date(lastLogin).fromNow();
    }
    return null;
  }, [ lastLogin ]);

  return (
    <>
      <Box className={className} data-testid="profile-header">
        <Box display="flex" flexDirection="row" gap="1rem" pb={6}>
          {avatarUrl && (
            <Box display="flex">
              <img src={avatarUrl} className={classes.profilePicture} alt={`${name} avatar`} />
            </Box>
          )}
          <Box display="flex" justifyContent="space-between" flexDirection="column" width="100%" height="112px">
            <Box display="flex" justifyContent="space-between">
              <Typography className={classes.profileLabel} data-testid="profile-label">
                {profileLabel}
              </Typography>
              <Box display="flex" gap="8px" alignItems="center">
                {chips}
                {Object.keys(options)?.length > 0 && <MoreOptions options={options} />}
              </Box>
            </Box>
            <Box display="flex" alignItems="baseline" gap="4px">
              <Typography variant="h3" className={classes.title} data-testid="profile-name">
                {name}
              </Typography>
              {preferredName && (
                <Typography color="textSecondary" data-testid="preferred-name">
                  ({preferredName})
                </Typography>
              )}
              {pronouns && (
                <Typography color="textSecondary" data-testid="pronouns">
                  ({pronouns})
                </Typography>
              )}
            </Box>
            <Box display="flex" alignItems="center" gap="8px">
              <Box display="flex" gap="8px" mr={2}>
                <Typography className={classes.boldText}>UUID </Typography>
                <ClickToCopy text={id} arrow copyIcon checkIcon>
                  <Typography className={classes.textSecondary}>{`${id.slice(0, 8)}...`}</Typography>
                </ClickToCopy>
              </Box>
              {providerNPI && (
                <Box display="flex" gap="8px" mr={2}>
                  <Typography className={classes.boldText}>NPI </Typography>
                  <ClickToCopy text={providerNPI} arrow copyIcon checkIcon>
                    <Typography className={classes.textSecondary}>{providerNPI}</Typography>
                  </ClickToCopy>
                </Box>
              )}
              {createdAt && (
                <Box display="flex" gap="8px" mr={2}>
                  <Typography className={classes.boldText}>Account Created</Typography>
                  <ClickToCopy text={createdAt && CalendarDateService.date(createdAt).tz(CalendarDateService.timezones.ET).format(DateFormats.DATETIME)} arrow copyIcon checkIcon>
                    <Typography className={classes.textSecondary}>{createdAt && CalendarDateService.date(createdAt).tz(CalendarDateService.timezones.ET).format(DateFormats.DATETIME)}</Typography>
                  </ClickToCopy>
                </Box>
              )}
              {upcomingAppointmentDate && (
                <Box display="flex" gap="8px" alignItems="center" className={cx(classes.noHover, classes.textSecondary)}>
                  <Event fontSize="small"/>
                  <Typography variant="body1">{upcomingAppointmentDate}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <DeactivateDialog
        userId={id}
        name={name}
        type={userRole}
        meta={meta}
        open={deactivateModalOpen}
        onClose={() => setDeactivateModalOpen(false)}
        onSubmit={() => {
          setDeactivateModalOpen(false);
          onActiveStatusChange?.(false);
        }}
      />
      <Box className={classes.tabsContainer} data-testid="profile-tabs">
        <Box className={classes.tabsWrapper}>
          <ProfileTabs tab={tab} tabs={tabs} onChange={onTabChange} />
          <Box display="flex" justifyContent="flex-end">
            {lastReferralDate && (
              <Box display="flex" alignItems="center" justifyContent="end" gap="8px">
                <Typography variant="body2" color="textSecondary">{lastReferralDate}</Typography>
              </Box>
            )}
            {lastLoginValue !== null && (
              <Box display="flex" alignItems="center" justifyContent="end" gap="8px">
                <Typography variant="body2" whiteSpace="nowrap" color="textSecondary">{`Last Logged in ${lastLoginValue}`}</Typography>
              </Box>
            )}
            {onImpersonate && (
              <AsyncButton
                endIcon={<OuterLinkIcon className={classes.impersonateIcon} />}
                onClick={onImpersonate}
              >
                Impersonate
              </AsyncButton>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

const useProfileHeaderStyles = makeStyles({ name: 'ProfileHeader' })((theme) => ({
  tabsContainer: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(0, 6),
    paddingLeft: theme.spacing(1),
  },
  tabsWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  impersonateIcon: {
    color: theme.palette.text.secondary,
    height: '15px',
    width: '15px',
  },
  profilePicture: {
    height: '112px',
    width: '112px',
  },
  title: {
    color: theme.palette.darkSlate.main,
  },
  profileLabel: {
    color: theme.palette.secondary.main,
    fontSize: '12px',
    fontWeight: 700,
    lineHeight: '24px',
    letterSpacing: '1px',
    textAlign: 'left',
    textTransform: 'uppercase',
  },
  boldText: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  textSecondary: {
    color: theme.palette.text.secondary,
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.5px',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      cursor: 'pointer',
    },
  },
  noHover: {
    '&:hover': {
      backgroundColor: 'transparent',
      cursor: 'default',
    },
  }
}));
